import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import {
  LayoutService,
  LpxAvatarModule,
  LpxBrandLogoModule,
  LpxIconModule,
  LpxNavbarModule,
  LpxResponsiveModule,
  LpxTranslateModule,
  NavbarService,
  UserProfileService,
} from '@volo/ngx-lepton-x.core';
import { MnLanguageSelectionModule } from '@volo/ngx-lepton-x.lite/layouts';
import { Notification } from '../notification/notification.component';

/**
 * Replacement for LeptonX Lite's own MobileNavbarComponent, registered against
 * `eThemeLeptonXComponents.MobileNavbar` (see mobile-navbar.provider.ts). The markup is a faithful
 * copy of the theme's — the layout bundle styles it entirely by class name — with two deliberate
 * differences.
 *
 * 1. The notification bell is a normal child of `.user-menu`, alongside the account and hamburger
 *    icons. Previously it was rendered as a sibling of `abp-dynamic-layout` in AppComponent and
 *    dragged into place with `position: absolute`, because on mobile the bundle hides
 *    `.lpx-topbar-content`, where the desktop bell lives. That left it outside the navbar's
 *    containing block, so it did not move with the sticky bar and scrolled off the top of the page.
 *
 * 2. `toggleUserMenu()` maintains the `mobile-menu-opened` layout class. The theme's version does
 *    not — only `toggleNavbar()` does:
 *
 *      toggleUserMenu() { this.userMenuHidden = !this.userMenuHidden; }              // <- missing
 *      toggleNavbar()   { this.navItemsHidden = !this.navItemsHidden; this.setLayoutClass(); }
 *
 *    That class is the whole overlay. LayoutService feeds `[ngClass]` on #lpx-wrapper, and the
 *    bundle hangs three rules off it: `overflow: hidden`, `.lpx-content-container { display: none }`
 *    and `.lpx-mobile-navbar-container { height: 100vh; display: block }`. Without it the account
 *    panel opens inside a zero-height container with the page still drawn over it, and because
 *    `.user-menu-groups` is `position: sticky; top: 72px`, the page appears frozen until it is
 *    scrolled roughly halfway. Hence "the account menu never opens".
 *
 * The two panels are also made mutually exclusive here, which the theme leaves undefined: opening
 * one closes the other, so the layout class always matches what is actually on screen.
 */
@Component({
    selector: 'app-mobile-navbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        LpxIconModule,
        LpxBrandLogoModule,
        LpxNavbarModule,
        LpxResponsiveModule,
        LpxAvatarModule,
        LpxTranslateModule,
        RouterModule,
        MnLanguageSelectionModule,
        Notification,
    ],
    templateUrl: './mobile-navbar.component.html',
    styleUrl: './mobile-navbar.component.scss'
})
export class AppMobileNavbarComponent {
  readonly navService = inject(NavbarService);
  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mobileMenuOpened = 'mobile-menu-opened';

  /**
   * Routes that show no bell. Carried over from the `hideNotificaiton` flag AppComponent used to
   * apply to the floating bell, with two corrections: the old test was
   * `['/', '/new-claim'].some(item => item.includes(location.pathname))` — the operands are the
   * wrong way round, so it only ever matched '/' — and it was evaluated once in ngOnInit rather
   * than per navigation, so it went stale as soon as the user moved between routes.
   */
  private static readonly hiddenOn = ['/', '/new-claim'];

  /** Same source LeptonX's own MnUserProfileComponent reads; ABP populates it. */
  readonly user$ = inject(UserProfileService).user$;

  // A signal: this bar sits inside the OnPush `lpx-layout`, so a field set on NavigationEnd is
  // not redrawn after a navigation that no click started, such as a redirect after saving (#34).
  readonly showNotification = signal(true);
  userMenuHidden = true;
  navItemsHidden = true;

  /**
   * The theme collapses the action list behind a second tap on the name row. Opened by default
   * here: this panel is a dedicated full-screen surface reached by tapping the account icon, so
   * the actions are the whole point of being on it. The row still toggles.
   */
  profileMenuHidden = false;

  toggleProfileMenu(): void {
    this.profileMenuHidden = !this.profileMenuHidden;
  }

  onActionClick(action: { action?: () => void }): void {
    action.action?.();
  }

  constructor() {
    this.updateNotificationVisibility(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => this.updateNotificationVisibility(event.urlAfterRedirects));
  }

  private updateNotificationVisibility(url: string): void {
    const path = url.split(/[?#]/)[0];
    this.showNotification.set(
      !AppMobileNavbarComponent.hiddenOn.some(route => path === route || path.startsWith(`${route}/`))
    );
  }

  ngOnDestroy(): void {
    this.layoutService.removeClass(this.mobileMenuOpened);
  }

  toggleUserMenu(): void {
    this.userMenuHidden = !this.userMenuHidden;
    this.navItemsHidden = true;
    this.setLayoutClass();
  }

  toggleNavbar(): void {
    this.navItemsHidden = !this.navItemsHidden;
    this.userMenuHidden = true;
    this.setLayoutClass();
  }

  private setLayoutClass(): void {
    if (this.userMenuHidden && this.navItemsHidden) {
      this.layoutService.removeClass(this.mobileMenuOpened);
    } else {
      this.layoutService.addClass(this.mobileMenuOpened);
    }
  }
}
